import { create } from "zustand";
import { SurveyPlatform, SurveySettings } from "../types/common";
import localStorageService from "@/shared/services/localStorageService";
import surveyService from "../services/surveyService";
import { Question, Survey } from "../types/valuebuilder";
import createSelectors from "@/shared/utils/createSelectors";

interface SurveyStoreState {
  isLoading: boolean;
  questions: Question[];
  survey: Survey | null;
  isEmployee: boolean;
  surveyLoadingError: boolean;
  settings: SurveySettings | null;
}
export interface SurveyStore extends SurveyStoreState {
  initSurvey: (
    uId: string,
    platform: SurveyPlatform,
    isPreview: boolean
  ) => Promise<void>;
  updateQuestions: (questions: Question[]) => void;
  updateSettings: (settings: SurveySettings) => void;
  resetState: () => void;
  clearSettings: () => void;
}

const initialState: SurveyStoreState = {
  isLoading: false,
  questions: [],
  survey: null,
  isEmployee: false,
  surveyLoadingError: false,
  settings: null,
};

const useSurveyStoreBase = create<SurveyStore>((set, get) => ({
  ...initialState,
  settings: localStorageService.getItem<SurveySettings>("surveySettings"),

  initSurvey: async (uid, platform, isPreview) => {
    set({ isLoading: true, surveyLoadingError: false });
    const { settings, updateSettings } = get();

    try {
      // Validate parameters first
      surveyService.validateSurveyInitParams(uid, platform);
      console.log({ uid, platform, isPreview });
      // Use service to handle all initialization logic
      const result = await (isPreview
        ? surveyService.initializePreviewSurvey(uid, platform, settings)
        : surveyService.initializeSurvey(uid, platform, settings));

      // Update store with results
      set({
        survey: result.survey,
        isEmployee: result.survey.stepType === "hub_and_spoke",
        questions: result.questions,
        surveyLoadingError: false,
        isLoading: false,
      });

      // Update settings if provided
      if (result.settings) {
        updateSettings(result.settings);
      }
    } catch (error) {
      console.error("Failed to initialize survey:", error);
      set({
        survey: null,
        questions: [],
        surveyLoadingError: true,
        isLoading: false,
      });
    }
  },
  updateQuestions: (questions) => {
    set({ questions });
  },

  updateSettings: (settings: SurveySettings) => {
    set({ settings });
    localStorageService.setItem("surveySettings", settings);
  },
  clearSettings: () => {
    set({ settings: null });
    localStorageService.removeItem("surveySettings");
  },
  resetState: () => {
    set(initialState);
    localStorageService.removeItem("surveySettings");
  },
}));

const useSurveyStore = createSelectors(useSurveyStoreBase);

export default useSurveyStore;
